import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoading } from '../contexts/LoadingContext'
import { ApplicationWizard, JobApplication } from '../components/ApplicationWizard'
import { jobApplicationService } from '../services/jobApplicationService'
import { useMessageBox } from '../components/MessageBox'
import { ButtonLoader } from '../components/ButtonLoader'
import { useResumes } from '../hooks/useResumes'
import { useSubscription } from '../hooks/useSubscription'
import jobApplicationsImage from '../images/JobApplicationsImage.png'
import resumesImage from '../images/ResumesImage.png'
import careerDevelopmentImage from '../images/CareerDevelopmentImage.png'
import './DashboardPage.css'

export const DashboardPage: React.FC = () => {
  const { setLoading, setMultipleLoading, loadingStates } = useLoading();
  const hasInitialized = useRef(false);
  const navigate = useNavigate();
  const resumesHook = useResumes();
  const { resumes, isLoading: resumesLoading } = resumesHook;
  const { showMessageBox } = useMessageBox();
  const { getRemainingQuotas } = useSubscription();

  // ApplicationWizard state
  const [isWizardOpen, setIsWizardOpen] = useState(false);
   
  const [editingApplication, _setEditingApplication] = useState<JobApplication | null>(null);
   
  const [isViewMode, _setIsViewMode] = useState(false);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  
  const resumeCount = useMemo(() => resumes.length, [resumes.length]);
  
  // Calculate remaining quotas based on subscription
  const remainingQuotas = useMemo(() => {
    return getRemainingQuotas({
      resumes: resumeCount,
      jobApplications: applications.length,
    });
  }, [resumeCount, applications.length, getRemainingQuotas]);

  // Calculate remaining slots for display
  const remainingSlots = useMemo(() => {
    if (remainingQuotas.resumes === null) return null;
    return remainingQuotas.resumes;
  }, [remainingQuotas.resumes]);

  // When no resume, user can still view the list; only adding is gated on Applications page.
  const hasResume = resumeCount > 0;
  const applicationsGated = !hasResume && !resumesLoading;

  const handleOpenApplications = useCallback(() => {
    // Always go to applications so user can see their saved list; add-action is gated there.
    navigate('/applications');
  }, [navigate]);

  const handleOpenResumes = useCallback(() => {
    navigate('/resumes');
  }, [navigate]);

  // Load applications data
  const loadApplications = useCallback(async () => {
    try {
      setLoading('applications', true);
      const result = await jobApplicationService.getApplications();
      
      if (result && result.applications && Array.isArray(result.applications)) {
        const convertedApplications = result.applications.map((app: any) => 
          jobApplicationService.convertToJobApplication(app)
        );
        setApplications(convertedApplications);
      } else if (result && Array.isArray(result) && result.length === 0) {
        // Empty array is a valid state - no applications yet
        setApplications([]);
      } else if (result && (!result.applications || (Array.isArray(result.applications) && result.applications.length === 0))) {
        // No applications found is a valid state, not an error
        setApplications([]);
      } else {
        // Unexpected response structure - set empty array
        setApplications([]);
      }
    } catch (error) {
      // Set empty array on error to prevent UI issues
      setApplications([]);
    } finally {
      setLoading('applications', false);
    }
  }, [setLoading]);

  const handleSaveApplication = useCallback(async (application: JobApplication) => {
    setLoading('applications', true);
    try {
      // Trigger cross-tab refresh notification (for other tabs)
      localStorage.setItem('job_application_updated', Date.now().toString());
      
      if (application.id) {
        await jobApplicationService.updateApplication(application.id, application);
        showMessageBox({ 
          type: 'success', 
          title: 'Success', 
          message: 'Application updated successfully', 
          buttons: ['ok'],
          onButtonClick: () => {}
        });
      } else {
        await jobApplicationService.createApplication(application);
        showMessageBox({ 
          type: 'success', 
          title: 'Success', 
          message: 'Application created successfully', 
          buttons: ['ok'],
          onButtonClick: () => {}
        });
      }
      setIsWizardOpen(false);
      
      // Refresh applications list (same-tab refresh)
      await loadApplications();
      
      // Trigger cross-tab refresh notification again
      localStorage.setItem('job_application_updated', Date.now().toString());
    } catch (error) {
      showMessageBox({ 
        type: 'error', 
        title: 'Error', 
        message: 'Error saving application', 
        buttons: ['ok'],
        onButtonClick: () => {}
      });
    } finally {
      setLoading('applications', false);
    }
  }, [setLoading, showMessageBox, loadApplications]);

  // Load data when dashboard opens
  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Load applications data
    loadApplications();

    // Start loading multiple sections
    setMultipleLoading({
      analytics: true,
      insights: true
    });

    // Simulate loading analytics data
    setTimeout(() => {
      setLoading('analytics', false);
    }, 2000);

    // Simulate loading insights data
    setTimeout(() => {
      setLoading('insights', false);
    }, 3000);

  }, [setLoading, setMultipleLoading, loadApplications]);

  // Cross-tab refresh using localStorage events + polling fallback
  useEffect(() => {
    // Handle storage events from OTHER tabs (cross-tab communication)
    // Note: localStorage events only fire in OTHER tabs, not the tab that made the change
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'job_application_updated' && e.newValue) {
        loadApplications();
      }
    };

    // Handle custom event from extension content script (instant refresh)
    // Extension → Content Script → Custom Event → Main App
    const handleExtensionNotification = (_e: Event) => {
      loadApplications();
    };

    // Listen for storage events (cross-tab communication)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom event from extension (instant refresh)
    window.addEventListener('kairos-job-application-saved', handleExtensionNotification as EventListener);

    // Polling fallback: Refresh every 30 seconds when tab is visible
    // This catches:
    // 1. Extension → Main app (different origins, localStorage events don't work)
    // 2. Cases where localStorage events might be missed
    let pollInterval: NodeJS.Timeout | null = null;
    const startPolling = () => {
      if (document.visibilityState === 'visible') {
        pollInterval = setInterval(() => {
          loadApplications();
        }, 30000); // Poll every 30 seconds when tab is visible
      }
    };

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // Start/stop polling based on tab visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh immediately when tab becomes visible, then start polling
        loadApplications();
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start polling if tab is already visible
    if (document.visibilityState === 'visible') {
      startPolling();
    }

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('kairos-job-application-saved', handleExtensionNotification as EventListener);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [loadApplications]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">

        {/* Main Content Grid */}
        <div className="content-grid" data-tour-id="dashboard-quick-stats">
          {/* Applications & Resumes */}
          <div className="content-card applications-card">
            <div className="card-header">
              <div className="card-title-section">
                <h3 className="card-title">Applications & Resumes</h3>
                <p className="card-subtitle">Manage your job applications and resumes</p>
              </div>
            </div>
            <div className="card-content">
              <div className="management-buttons">
                <button 
                  className={`management-button applications-button ${loadingStates.applications ? 'button-loading' : ''} ${applicationsGated ? 'applications-gated-no-resume' : ''}`}
                  onClick={handleOpenApplications}
                  title={applicationsGated ? 'View your saved applications. Upload a resume to add new ones.' : 'Manage job applications'}
                >
                  <div className="button-icon">
                    <img 
                      src={jobApplicationsImage} 
                      alt="Job Applications" 
                      className="button-icon-image"
                    />
                  </div>
                  <div className="button-content">
                    <div className="button-label">Job Applications</div>
                    <div className="button-value">{applications.length}</div>
                    <div className="button-trend">
                      <span className="trend-icon">📈</span>
                      <span className="trend-text">{applicationsGated ? 'View applications' : 'Manage applications'}</span>
                    </div>
                  </div>
                  {loadingStates.applications && <ButtonLoader size="small" theme="blue" />}
                </button>

                <button 
                  className={`management-button resumes-button ${resumesLoading ? 'button-loading' : ''}`}
                  onClick={handleOpenResumes}
                >
                  <div className="button-icon">
                    <img 
                      src={resumesImage} 
                      alt="Resumes" 
                      className="button-icon-image"
                    />
                  </div>
                  <div className="button-content">
                    <div className="button-label">Resumes</div>
                    <div className="button-value">{resumeCount}</div>
                    {remainingSlots !== null && (
                      <div className="button-trend">
                        <span className="trend-icon">✨</span>
                        <span className="trend-text">
                          {`${remainingSlots} remaining`}
                        </span>
                      </div>
                    )}
                  </div>
                  {resumesLoading && <ButtonLoader size="small" theme="purple" />}
                </button>

                <button 
                  className="management-button career-development-button"
                  disabled
                >
                  <div className="button-icon">
                    <img 
                      src={careerDevelopmentImage} 
                      alt="Career Development" 
                      className="button-icon-image"
                    />
                  </div>
                  <div className="button-content">
                    <div className="button-label">Career Development</div>
                    <div className="button-value">—</div>
                    <div className="button-trend">
                      <span className="trend-icon">🔧</span>
                      <span className="trend-text">Under development</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Test credentials removed for security */}
      </div>
      
      {/* ApplicationWizard Modal */}
      {isWizardOpen && (
        <ApplicationWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          onSave={handleSaveApplication}
          editingApplication={editingApplication}
          viewMode={isViewMode}
        />
      )}
    </div>
  );
};

export default DashboardPage;
